import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  salaryStructureService,
  companyService,
  categoryService,
  branchService,
  employeeService,
  payrollOpsService,
} from "../../../../services/api";
import SalaryStructureCalculationResults from "./SalaryStructureCalculationResults";
import { templateToStructure } from "./utils/salaryStructurePersistence";
import { findApplicableSalaryStructure } from "../../../../utils/salaryStructureMatch";
import { buildEmployeeStructureBreakdown } from "../../../../utils/employeeStructureBreakdown";
import { resolvePtStateFromEmployee } from "./utils/ptStates";
import {
  normalizeSalaryStructureMethod,
  resolveStructureMode,
  matchProfilePackageAmount,
} from "../../../../utils/salaryStructureMethod";
import { getAuthItem, getAuthJson } from "../../../../utils/authStorage";
import {
  exportSalaryBreakdownToExcel,
  exportSalaryBreakdownToPdf,
} from "./utils/exportSalaryStructureBreakdown";
import { usePermissions } from "../../../../rbac/PermissionsContext";
import { DsButton } from "../../../../design-system";
import {
  applyManualOverrides,
  applyFormulaOverrides,
  minimumWageForStructure,
  extractManualOverridesFromSnapshot,
  structureCalcModeLabel,
  structureRequiresManualInput,
  resolveEmployeeStructureEffectiveFrom,
  employeeJoiningDateForStructure,
  toStructureDateOnly,
} from "../../../../utils/applyEmployeeStructureFromTemplate";
import { resolveFixedPackageCtc } from "../../../../utils/salaryStructureComponentRules";
import EmployeeStructureOptionsPanel from "./EmployeeStructureOptionsPanel";
import {
  normalizeNpsConfig,
  normalizeVpfConfig,
  normalizeMealVoucherConfig,
} from "../../../../utils/salaryStructureAddons";
function roundAmountSafe(n) {
  return Math.round(Number(n) || 0);
}

/**
 * Employee salary structure (eye modal):
 * - PB enable at employee level (Total CTC − PB = Fixed for breakdown)
 * - Manual Fixed heads editable for Admin (overrides)
 * - Formula / Balance CTC stay derived
 */
const EmployeeSalaryStructureDesigner = ({
  employee,
  darkMode = false,
  matchedStructure = null,
  orgSalaryMethod: orgMethodProp,
  onActionsChange,
  onEmployeePatch,
}) => {
  const [structure, setStructure] = useState(matchedStructure);
  const [loading, setLoading] = useState(!matchedStructure);
  const [mwAmount, setMwAmount] = useState(null);
  const [orgMethod, setOrgMethod] = useState(() =>
    normalizeSalaryStructureMethod(
      orgMethodProp || getAuthJson("companyData")?.defaultSalaryStructureMethod
    )
  );
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [statutoryConfigJson, setStatutoryConfigJson] = useState(null);
  const [branchesList, setBranchesList] = useState([]);

  const structureAllowsPb = !!structure?.performanceBonus?.enabled;
  const [pbEnabled, setPbEnabled] = useState(false);
  const [pbAmount, setPbAmount] = useState(
    () => String(employee?.performanceBonus ?? employee?.performanceBonusAmount ?? "")
  );
  const [pbPeriod, setPbPeriod] = useState(
    () => employee?.performanceBonusPeriod || "annual"
  );
  const [pbFrequency, setPbFrequency] = useState(
    () =>
      employee?.performanceBonusFrequency ||
      structure?.performanceBonus?.frequency ||
      "annual"
  );
  const [manualOverrides, setManualOverrides] = useState({});
  const [formulaOverrides, setFormulaOverrides] = useState({});
  const [overrideDraft, setOverrideDraft] = useState(null);
  const { hasScreenAccess } = usePermissions();
  const canOverrideFormula = hasScreenAccess("payroll", "structure_value_override", "edit");
  const [snapshotHydrated, setSnapshotHydrated] = useState(false);
  const [structureModeLabel, setStructureModeLabel] = useState("");
  const [statutoryFlags, setStatutoryFlags] = useState(() => ({
    pfApplicable: employee?.pfApplicable !== false,
    ceilingPf: employee?.ceilingPf !== false,
    epsApplicable: !!employee?.epsApplicable,
    esiApplicable: !!employee?.esiApplicable,
    ptApplicable: employee?.ptApplicable !== false,
    gratuityApplicable: employee?.gratuityApplicable !== false,
  }));
  const [employeeNps, setEmployeeNps] = useState({ enabled: false });
  const [employeeVpf, setEmployeeVpf] = useState({ enabled: false });
  const [employeeMeal, setEmployeeMeal] = useState({ enabled: false });
  const [structureLock, setStructureLock] = useState({
    locked: false,
    lockReason: null,
    minEffectiveMonth: null,
  });
  /** Active assignment effective month (YYYY-MM) — drives mid-month PF package for Profile. */
  const [structureEffectiveMonth, setStructureEffectiveMonth] = useState("");

  // PB only when structure enables it
  useEffect(() => {
    if (!structureAllowsPb) {
      setPbEnabled(false);
      return;
    }
    const hasPb =
      Number(employee?.performanceBonus || employee?.performanceBonusAmount || 0) > 0;
    setPbEnabled(hasPb);
  }, [structureAllowsPb, employee?.performanceBonus, employee?.performanceBonusAmount]);

  const annualAmount = Number(employee?.ctc || employee?.ctcAmount || 0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statRes, branchRes] = await Promise.all([
          salaryStructureService.getStatutoryConfig(),
          branchService.getBranches(),
        ]);
        if (cancelled) return;
        if (statRes?.data?.configJson) {
          setStatutoryConfigJson(statRes.data.configJson);
        }
        if (branchRes?.success) {
          setBranchesList(branchRes.data || []);
        }
      } catch {
        if (!cancelled) {
          setStatutoryConfigJson(null);
          setBranchesList([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (orgMethodProp) {
      setOrgMethod(normalizeSalaryStructureMethod(orgMethodProp));
    }
  }, [orgMethodProp]);

  useEffect(() => {
    if (matchedStructure) {
      setStructure(matchedStructure);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await salaryStructureService.listTemplates();
        const rows = (Array.isArray(res?.data) ? res.data : [])
          .map(templateToStructure)
          .filter(Boolean);
        const matched = findApplicableSalaryStructure(employee, rows);
        if (!cancelled) setStructure(matched);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.message || "Failed to load salary structures");
          setStructure(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [employee, matchedStructure]);

  // Hydrate Manual overrides + addons from saved employee snapshot (not blank template defaults).
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      setSnapshotHydrated(false);
      setManualOverrides({});
      setFormulaOverrides({});
      setOverrideDraft(null);
      if (!structure || !employee?.id) {
        setStructureModeLabel("");
        return;
      }
      setStructureModeLabel(structureCalcModeLabel(structure));
      try {
        const [res, lockRes, resolveRes] = await Promise.all([
          salaryStructureService.getActiveStructure(employee.id),
          payrollOpsService.getStructureEditLock(employee.id).catch(() => null),
          payrollOpsService
            .resolveStatutoryApplicability(employee.id)
            .catch(() => null),
        ]);
        const active = res?.data || null;
        if (cancelled) return;
        const effRaw = active?.effectiveFrom || active?.effective_from || "";
        const effYm = String(effRaw).slice(0, 7);
        setStructureEffectiveMonth(/^\d{4}-\d{2}$/.test(effYm) ? effYm : "");
        if (lockRes?.data) {
          setStructureLock({
            locked: !!lockRes.data.locked,
            lockReason: lockRes.data.lockReason || null,
            minEffectiveMonth: lockRes.data.minEffectiveMonth || null,
          });
        }
        if (!active) {
          setStructureEffectiveMonth("");
          // Still apply resolved benefits so joining/profile CTC can include NPS/Meal
          const benefits = resolveRes?.data?.benefits || resolveRes?.benefits || null;
          if (benefits?.employerNps) {
            setEmployeeNps({
              ...(typeof benefits.employerNps === "object" ? benefits.employerNps : {}),
              enabled: benefits.employerNps.enabled === true,
            });
          }
          if (benefits?.mealVoucher) {
            setEmployeeMeal({
              ...(typeof benefits.mealVoucher === "object" ? benefits.mealVoucher : {}),
              enabled: benefits.mealVoucher.enabled === true,
            });
          }
          setManualOverrides({});
          setSnapshotHydrated(true);
          return;
        }
        const overrides = extractManualOverridesFromSnapshot(active, structure);
        setManualOverrides(overrides);
        const params =
          typeof active.parametersJson === "string"
            ? JSON.parse(active.parametersJson || "{}")
            : active.parametersJson || {};
        const addons = params.structureAddons || params;
        if (
          params.formulaOverrides &&
          typeof params.formulaOverrides === "object" &&
          Number(active.templateId ?? active.template_id ?? params.structureId) === Number(structure.id)
        ) {
          setFormulaOverrides(params.formulaOverrides);
        }
        if (params.statutoryApplicability) {
          setStatutoryFlags((prev) => ({
            ...prev,
            ...params.statutoryApplicability,
          }));
        }
        const benefits = resolveRes?.data?.benefits || resolveRes?.benefits || null;
        if (benefits?.employerNps) {
          setEmployeeNps({
            ...(typeof benefits.employerNps === "object" ? benefits.employerNps : {}),
            enabled: benefits.employerNps.enabled === true,
          });
        } else if (addons.employeeNps) {
          setEmployeeNps({ enabled: false, ...addons.employeeNps });
        }
        if (addons.employeeVpf) setEmployeeVpf({ enabled: false, ...addons.employeeVpf });
        if (benefits?.mealVoucher) {
          setEmployeeMeal({
            ...(typeof benefits.mealVoucher === "object" ? benefits.mealVoucher : {}),
            enabled: benefits.mealVoucher.enabled === true,
          });
        } else if (addons.employeeMealVoucher || addons.employeeMeal) {
          setEmployeeMeal({
            enabled: false,
            ...(addons.employeeMealVoucher || addons.employeeMeal),
          });
        }
      } catch {
        if (!cancelled) setManualOverrides({});
      } finally {
        if (!cancelled) setSnapshotHydrated(true);
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [structure?.id, employee?.id]);

  useEffect(() => {
    setStatutoryFlags({
      pfApplicable: employee?.pfApplicable !== false,
      ceilingPf: employee?.ceilingPf !== false,
      epsApplicable: !!employee?.epsApplicable,
      esiApplicable: !!employee?.esiApplicable,
      ptApplicable: employee?.ptApplicable !== false,
      gratuityApplicable: employee?.gratuityApplicable !== false,
    });
  }, [
    employee?.id,
    employee?.pfApplicable,
    employee?.ceilingPf,
    employee?.epsApplicable,
    employee?.esiApplicable,
    employee?.ptApplicable,
    employee?.gratuityApplicable,
  ]);

  useEffect(() => {
    if (structure?.performanceBonus?.frequency && !employee?.performanceBonusFrequency) {
      setPbFrequency(structure.performanceBonus.frequency);
    }
  }, [structure?.performanceBonus?.frequency, employee?.performanceBonusFrequency]);

  useEffect(() => {
    let cancelled = false;
    const loadMw = async () => {
      const companyId =
        Number(getAuthItem("companyId")) ||
        Number(getAuthJson("currentUser")?.companyId) ||
        Number(getAuthJson("companyData")?.id) ||
        0;
      const categoryId = Number(employee?.categoryId);
      if (!companyId || !categoryId) return;
      try {
        const setting = await companyService.getMinimumWagesSetting(companyId);
        const follow = !!(setting?.data?.followMinimumWages);
        if (!follow) {
          if (!cancelled) setMwAmount(null);
          return;
        }
        const month = new Date().toISOString().slice(0, 7);
        const res = await categoryService.resolveMinimumWage(
          categoryId,
          month,
          employee?.branchId || employee?.branch_id || null
        );
        const amount = res?.data?.amount ?? res?.amount;
        if (!cancelled) {
          setMwAmount(amount != null && Number.isFinite(Number(amount)) ? Number(amount) : null);
        }
      } catch {
        if (!cancelled) setMwAmount(null);
      }
    };
    loadMw();
    return () => {
      cancelled = true;
    };
  }, [employee?.categoryId, employee?.branchId]);

  // Structure template type drives Gross vs CTC match (source of truth for this breakdown).
  // Org method only used when structure type is missing.
  const structurePreferred =
    structure?.salaryType === "gross" || structure?.salaryType === "GROSS"
      ? "GROSS"
      : structure?.salaryType === "ctc" || structure?.salaryType === "CTC"
        ? "CTC"
        : null;
  const mode = structurePreferred || resolveStructureMode(orgMethod, "CTC");
  const salaryType = mode === "GROSS" ? "gross" : "ctc";

  const formulaBaseStructure = useMemo(() => {
    if (!structure) return null;
    return applyManualOverrides(structure, manualOverrides);
  }, [structure, manualOverrides]);

  const effectiveStructure = useMemo(
    () => (formulaBaseStructure ? applyFormulaOverrides(formulaBaseStructure, formulaOverrides) : null),
    [formulaBaseStructure, formulaOverrides]
  );

  const employeeForBreakdown = useMemo(() => {
    const usePb = structureAllowsPb && pbEnabled;
    return {
      ...employee,
      ...statutoryFlags,
      performanceBonus: usePb ? Number(pbAmount) || 0 : 0,
      performanceBonusPeriod: pbPeriod || "annual",
      performanceBonusFrequency: pbFrequency || "annual",
      employeeNps,
      employeeMealVoucher: employeeMeal,
      employeeMeal,
    };
  }, [
    employee,
    pbEnabled,
    pbAmount,
    pbPeriod,
    pbFrequency,
    structureAllowsPb,
    statutoryFlags,
    employeeNps,
    employeeMeal,
  ]);

  const packageSplit = useMemo(() => {
    if (!structureAllowsPb || !pbEnabled || !(Number(pbAmount) > 0)) {
      return resolveFixedPackageCtc({
        totalAmount: annualAmount,
        totalPeriod: "annual",
        performanceBonusAmount: 0,
      });
    }
    return resolveFixedPackageCtc({
      totalAmount: annualAmount,
      totalPeriod: "annual",
      performanceBonusAmount: Number(pbAmount) || 0,
      performanceBonusPeriod: pbPeriod === "monthly" ? "monthly" : "annual",
    });
  }, [annualAmount, structureAllowsPb, pbEnabled, pbAmount, pbPeriod]);

  const [breakdown, formulaBreakdown] = useMemo(() => {
    if (!effectiveStructure || !annualAmount) return [null, null];
    const run = (s) =>
      buildEmployeeStructureBreakdown({
        structure: {
          ...s,
          performanceBonus: {
            enabled: structureAllowsPb && pbEnabled,
            frequency: pbFrequency,
          },
          effectiveMonth: structureEffectiveMonth || undefined,
        },
        annualAmount,
        salaryType,
        employee: employeeForBreakdown,
        minimumWageAmount: minimumWageForStructure(s, mwAmount),
        statutoryConfigJson,
        effectiveMonth: structureEffectiveMonth || undefined,
        branchesList,
        ptState: resolvePtStateFromEmployee(employee, branchesList),
      });
    const main = run(effectiveStructure);
    return [
      main,
      effectiveStructure === formulaBaseStructure ? main : run(formulaBaseStructure),
    ];
  }, [
    effectiveStructure,
    formulaBaseStructure,
    annualAmount,
    salaryType,
    employeeForBreakdown,
    employee,
    mwAmount,
    statutoryConfigJson,
    structureEffectiveMonth,
    branchesList,
    pbEnabled,
    pbFrequency,
  ]);

  const ctcMatch = useMemo(() => {
    if (!breakdown) {
      return {
        ok: true,
        profileAnnual: annualAmount,
        totalCtcAnnual: 0,
        matchedAnnual: 0,
        targetAnnual: annualAmount,
        delta: 0,
        matchedLabel: "Total CTC (E + B)",
        profileLabel: "Profile CTC",
        isGrossMode: mode === "GROSS",
      };
    }
    const profileAnnual = roundAmountSafe(annualAmount);
    const totalCtcAnnual = roundAmountSafe(
      breakdown.totals?.actualCtcAnnual ??
        (Number(breakdown.totals?.gross || 0) + Number(breakdown.totals?.employerShares || 0)) *
          12
    );
    // Subtotal (E) = earnings + non-employer benefits (excludes reimbursements & employer B)
    const earningsPart = Number(breakdown.totals?.earnings || 0);
    const benefitsPart = Number(breakdown.totals?.benefits || 0);
    const subtotalEMonthly =
      breakdown.totals?.earningsSubtotal != null &&
      Number.isFinite(Number(breakdown.totals.earningsSubtotal))
        ? Number(breakdown.totals.earningsSubtotal)
        : earningsPart + benefitsPart;
    // Prefer gross field when earningsSubtotal missing — never fold employer B into Gross match
    const grossAnnual = roundAmountSafe(
      breakdown.totals?.earningsSubtotalAnnual != null
        ? Number(breakdown.totals.earningsSubtotalAnnual)
        : subtotalEMonthly > 0
          ? subtotalEMonthly * 12
          : breakdown.totals?.grossAnnual ?? Number(breakdown.totals?.gross || 0) * 12
    );
    // With PB: breakdown is on Fixed package, so match against Fixed (Profile − PB)
    const targetAnnual =
      structureAllowsPb && pbEnabled
        ? roundAmountSafe(packageSplit.fixedAnnual)
        : profileAnnual;
    const match = matchProfilePackageAmount({
      mode,
      profileAmount: targetAnnual,
      grossAmount: grossAnnual,
      totalCtcAmount: totalCtcAnnual,
      hasReimbursements: Number(breakdown.totals?.reimbursements || 0) > 0,
      tolerance: 1,
    });
    return {
      ok: match.ok,
      profileAnnual,
      totalCtcAnnual,
      matchedAnnual: match.matchedAnnual,
      targetAnnual: match.targetAnnual,
      delta: match.delta,
      matchedLabel: match.matchedLabel,
      profileLabel:
        structureAllowsPb && pbEnabled
          ? mode === "GROSS"
            ? "Fixed Gross (Profile − PB)"
            : "Fixed CTC (Profile − PB)"
          : match.profileLabel,
      isGrossMode: match.isGrossMode,
      withPb: structureAllowsPb && pbEnabled,
    };
  }, [
    breakdown,
    annualAmount,
    structureAllowsPb,
    pbEnabled,
    packageSplit.fixedAnnual,
    mode,
  ]);

  const handleManualAmountChange = ({ name, componentId, value }) => {
    setManualOverrides((prev) => {
      const next = { ...prev };
      if (componentId != null && componentId !== "") {
        next[String(componentId)] = value;
      }
      if (name) next[String(name)] = value;
      return next;
    });
  };

  const canEditFormulaOverrides = canOverrideFormula && !structureLock.locked;
  const formulaOverrideProps = useMemo(() => {
    if (!formulaBreakdown) return null;
    const formulaAmounts = formulaBreakdown.amountsByComponentId || {};
    const balanceId =
      formulaBreakdown.balanceComponentId != null ? String(formulaBreakdown.balanceComponentId) : "";
    // Formula / earlier Balance CTC heads only — Manual heads are already editable, final balancer absorbs.
    const overridableIds = [
      ...formulaBreakdown.earningsList,
      ...formulaBreakdown.benefitsList,
      ...formulaBreakdown.reimbursementsList,
    ]
      .filter((r) => r.componentId != null && !r.editable && String(r.componentId) !== balanceId)
      .map((r) => String(r.componentId));
    return {
      entries: formulaOverrides,
      formulaAmounts,
      overridableIds,
      canEdit: canEditFormulaOverrides,
      onOverride: (item) => {
        const id = String(item.componentId);
        const existing = formulaOverrides[id];
        setOverrideDraft({
          componentId: id,
          name: item.name,
          formulaAmount: roundAmountSafe(formulaAmounts[id]),
          amount: String(roundAmountSafe(existing ? existing.amount : item.amount)),
          reason: existing?.reason || "",
          isExisting: !!existing,
        });
      },
    };
  }, [formulaBreakdown, formulaOverrides, canEditFormulaOverrides]);

  const resetOverrideDraft = () => {
    if (!overrideDraft) return;
    setFormulaOverrides((prev) => {
      const next = { ...prev };
      delete next[overrideDraft.componentId];
      return next;
    });
    toast.success(`${overrideDraft.name} reset to formula — Save to apply`);
    setOverrideDraft(null);
  };

  const applyOverrideDraft = () => {
    if (!overrideDraft || !canEditFormulaOverrides) return;
    const amount = Number(String(overrideDraft.amount).replace(/,/g, "").trim());
    const reason = String(overrideDraft.reason || "").trim();
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid monthly amount (0 or more)");
      return;
    }
    if (reason.length < 5) {
      toast.error("Reason is mandatory (at least 5 characters)");
      return;
    }
    const me = getAuthJson("currentUser") || {};
    setFormulaOverrides((prev) => ({
      ...prev,
      [overrideDraft.componentId]: {
        amount: roundAmountSafe(amount),
        formulaAmount: overrideDraft.formulaAmount,
        name: overrideDraft.name,
        reason,
        by: me.id ?? null,
        byName: me.name || me.fullName || me.email || "",
        at: new Date().toISOString(),
      },
    }));
    setOverrideDraft(null);
  };

  const handleSave = async () => {
    if (!employee?.id || !structure || !breakdown) return false;
    if (structureLock.locked) {
      toast.error(
        structureLock.lockReason ||
          "Structure is locked — use Salary Revision / Update Statutory"
      );
      return false;
    }
    if (packageSplit.error) {
      toast.error(packageSplit.error);
      return false;
    }
    if (!ctcMatch.ok) {
      const label = ctcMatch.profileLabel;
      toast.error(
        `${ctcMatch.matchedLabel} ₹${ctcMatch.matchedAnnual.toLocaleString("en-IN")} must match ${label} ₹${ctcMatch.targetAnnual.toLocaleString("en-IN")}. Adjust Manual monthly amounts until they match.`
      );
      return false;
    }
    setSaving(true);
    setSaveMsg("");
    try {
      const pbPayload = {
        performanceBonus:
          structureAllowsPb && pbEnabled ? Number(pbAmount) || 0 : null,
        performanceBonusPeriod: pbPeriod || "annual",
        performanceBonusFrequency: pbFrequency || "annual",
        pfApplicable: statutoryFlags.pfApplicable !== false,
        ceilingPf: statutoryFlags.ceilingPf !== false,
        epsApplicable: !!statutoryFlags.epsApplicable,
        esiApplicable: !!statutoryFlags.esiApplicable,
        ptApplicable: statutoryFlags.ptApplicable !== false,
        gratuityApplicable: statutoryFlags.gratuityApplicable !== false,
      };
      try {
        await employeeService.updateEmployee(employee.id, pbPayload);
        onEmployeePatch?.(pbPayload);
      } catch (pbErr) {
        console.warn("Employee profile update failed", pbErr);
      }

      const structureNps = normalizeNpsConfig(structure.nps);
      const structureVpf = normalizeVpfConfig(structure.vpf);
      const structureMeal = normalizeMealVoucherConfig(structure.mealVoucher);

      // Keep existing active effective date on re-save; first save → joining date.
      // Never stamp wall-clock "today" (breaks earlier pay months for Manual structures).
      let activeEffectiveFrom = null;
      try {
        const activeRes = await salaryStructureService.getActiveStructure(employee.id);
        activeEffectiveFrom =
          activeRes?.data?.effectiveFrom ||
          activeRes?.data?.effective_from ||
          null;
      } catch {
        /* first save — no active row yet */
      }
      const effectiveFrom = resolveEmployeeStructureEffectiveFrom({
        activeEffectiveFrom,
        joiningDate: employeeJoiningDateForStructure(employee),
      });

      await salaryStructureService.saveEmployeeStructure({
        employeeId: employee.id,
        templateId: structure.id,
        targetAnnualCtc: annualAmount,
        mode,
        cityType: structure.defaultCityType || "METRO",
        parametersJson: {
          structureId: structure.id,
          structureName: structure.name,
          statutoryApplicability: {
            ...breakdown.applicability,
            ...statutoryFlags,
          },
          manualOverrides,
          ...(Object.keys(formulaOverrides).length ? { formulaOverrides } : {}),
          performanceBonus: {
            enabled: structureAllowsPb && pbEnabled,
            amount: structureAllowsPb && pbEnabled ? Number(pbAmount) || 0 : 0,
            period: pbPeriod,
            frequency: pbFrequency,
            fixedAnnual: packageSplit.fixedAnnual,
            totalAnnual: packageSplit.totalAnnual,
          },
          // Structure enables + employee opt-ins (payroll readStructureAddons)
          nps: structureNps,
          vpf: structureVpf,
          mealVoucher: structureMeal,
          employeeNps: employeeNps?.enabled
            ? { ...employeeNps, enabled: true }
            : { enabled: false },
          employeeVpf: employeeVpf?.enabled
            ? { ...employeeVpf, enabled: true }
            : { enabled: false },
          employeeMealVoucher: employeeMeal?.enabled
            ? { ...employeeMeal, enabled: true }
            : { enabled: false },
          structureAddons: {
            nps: structureNps,
            vpf: structureVpf,
            mealVoucher: structureMeal,
            employeeNps: employeeNps?.enabled
              ? { ...employeeNps, enabled: true }
              : { enabled: false },
            employeeVpf: employeeVpf?.enabled
              ? { ...employeeVpf, enabled: true }
              : { enabled: false },
            employeeMealVoucher: employeeMeal?.enabled
              ? { ...employeeMeal, enabled: true }
              : { enabled: false },
          },
        },
        resolvedComponentsJson: [
          ...breakdown.earningsList,
          ...breakdown.benefitsList,
          ...breakdown.reimbursementsList,
          ...breakdown.deductionsList,
          ...(breakdown.statutoryDeductionsList || []).map((r) => ({
            ...r,
            category: "DEDUCTION",
            type: "Deduction",
          })),
          ...(breakdown.statutoryEmployerList || []).map((r) => ({
            ...r,
            category: "EMPLOYER_BENEFIT",
            type: "Employer",
          })),
        ],
        computedSummaryJson: {
          ...breakdown.totals,
          monthlyCtc: breakdown.monthlyCtc,
          annualAmount,
          fixedAnnualCtc: packageSplit.fixedAnnual,
          performanceBonusAnnual: packageSplit.pbAnnual,
          structureName: structure.name,
          statutoryDeductionsList: breakdown.statutoryDeductionsList || [],
          statutoryEmployerList: breakdown.statutoryEmployerList || [],
          nps: structureNps,
          vpf: structureVpf,
          mealVoucher: structureMeal,
        },
        complianceReportJson: [],
        effectiveFrom:
          toStructureDateOnly(effectiveFrom) ||
          resolveEmployeeStructureEffectiveFrom({
            joiningDate: employeeJoiningDateForStructure(employee),
          }),
        status: "active",
        changeNote: `Applied structure: ${structure.name}${pbEnabled ? " (with PB)" : ""}${
          Object.keys(formulaOverrides).length
            ? ` · Overrides: ${Object.values(formulaOverrides)
                .map((o) => `${o.name} ₹${o.amount} (${o.reason})`)
                .join("; ")}`
            : ""
        }`,
      });
      setSaveMsg("Structure saved for employee.");
      toast.success("Salary structure saved");
      return true;
    } catch (e) {
      const msg = e?.message || "Failed to save";
      setSaveMsg(msg);
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = async () => {
    if (!breakdown || !structure) {
      toast.error("Nothing to export");
      return;
    }
    setExporting(true);
    try {
      const filename = await exportSalaryBreakdownToExcel({
        employee,
        structure,
        breakdown,
      });
      toast.success(`Excel exported: ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Excel export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = () => {
    if (!breakdown || !structure) {
      toast.error("Nothing to export");
      return;
    }
    setExporting(true);
    try {
      const filename = exportSalaryBreakdownToPdf({
        employee,
        structure,
        breakdown,
      });
      toast.success(`PDF exported: ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!onActionsChange) return undefined;
    let saveDisabledReason = null;
    if (!structure) saveDisabledReason = "No matched salary structure";
    else if (!annualAmount) saveDisabledReason = "Profile Gross/CTC amount is missing";
    else if (loading) saveDisabledReason = "Loading…";
    else if (packageSplit.error) saveDisabledReason = packageSplit.error;
    else if (structureLock.locked)
      saveDisabledReason =
        structureLock.lockReason ||
        "Structure locked after payroll process/finalise — use Salary Revision";
    else if (!ctcMatch.ok)
      saveDisabledReason = `${ctcMatch.matchedLabel} ₹${Number(
        ctcMatch.matchedAnnual || 0
      ).toLocaleString("en-IN")} must equal ${ctcMatch.profileLabel} ₹${Number(
        ctcMatch.targetAnnual || 0
      ).toLocaleString("en-IN")} (diff ₹${Math.abs(ctcMatch.delta || 0).toLocaleString("en-IN")})`;
    else if (!breakdown) saveDisabledReason = "Breakdown not ready";

    onActionsChange({
      canSave: Boolean(
        structure &&
          annualAmount &&
          breakdown &&
          !loading &&
          !packageSplit.error &&
          ctcMatch.ok &&
          !structureLock.locked
      ),
      saveDisabledReason,
      saving,
      exporting,
      save: handleSave,
      saveMsg,
      exportExcel: handleExportExcel,
      exportPdf: handleExportPdf,
      canExport: Boolean(structure && annualAmount && breakdown && !loading),
      mode,
    });
    return () => onActionsChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onActionsChange,
    structure,
    annualAmount,
    breakdown,
    loading,
    saving,
    exporting,
    saveMsg,
    manualOverrides,
    formulaOverrides,
    pbEnabled,
    pbAmount,
    pbPeriod,
    pbFrequency,
    packageSplit.error,
    ctcMatch.ok,
    ctcMatch.matchedAnnual,
    ctcMatch.targetAnnual,
    ctcMatch.delta,
    ctcMatch.matchedLabel,
    ctcMatch.profileLabel,
    structureLock.locked,
    structureLock.lockReason,
    statutoryFlags,
    employeeNps,
    employeeVpf,
    employeeMeal,
    mode,
  ]);

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-[hsl(var(--ds-primary))]" />
      </div>
    );
  }

  if (!structure) {
    return (
      <div
        className={`rounded-xl border border-dashed px-4 py-10 text-center text-sm ${
          darkMode
            ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
        }`}
      >
        Salary structure not assigned for this employee.
      </div>
    );
  }

  if (!annualAmount) {
    return (
      <div
        className={`rounded-xl border px-4 py-8 text-center text-sm ${
          darkMode
            ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            : "border-amber-200 bg-amber-50 text-amber-900"
        }`}
      >
        Profile {mode === "GROSS" ? "Gross" : "CTC"} is not set. Save the annual amount on Org Details first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className={`text-sm font-semibold ${darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"}`}>
          {structure.name}
          <span className="ml-2 text-xs font-normal text-[hsl(var(--ds-muted-fg))]">
            {mode} · Profile ₹{annualAmount.toLocaleString("en-IN")}/yr
          </span>
          {structureModeLabel ? (
            <span
              className={`ml-2 inline-flex align-middle items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                structureRequiresManualInput(structure)
                  ? "bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning))]"
                  : "bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]"
              }`}
            >
              {structureModeLabel}
            </span>
          ) : null}
        </p>
      </div>

      <EmployeeStructureOptionsPanel
        structure={structure}
        employee={employee}
        readOnly={structureLock.locked}
        lockReason={structureLock.lockReason}
        minEffectiveMonth={structureLock.minEffectiveMonth}
        statutoryFlags={statutoryFlags}
        onStatutoryChange={(next, meta) => {
          setStatutoryFlags(next || {});
          if (meta?.effectiveFrom) {
            onEmployeePatch?.({
              ...(next || {}),
              statutoryEffectiveFrom: meta.effectiveFrom,
            });
          }
        }}
        employeeNps={employeeNps}
        employeeVpf={employeeVpf}
        employeeMeal={employeeMeal}
        onEmployeeAddonChange={(key, payload) => {
          if (key === "nps") setEmployeeNps(payload);
          else if (key === "vpf") setEmployeeVpf(payload);
          else if (key === "meal") setEmployeeMeal(payload);
        }}
        structureAllowsPb={structureAllowsPb}
        pbEnabled={pbEnabled}
        onPbEnabledChange={setPbEnabled}
        pbAmount={pbAmount}
        onPbAmountChange={setPbAmount}
        pbPeriod={pbPeriod}
        onPbPeriodChange={setPbPeriod}
        pbFrequency={pbFrequency}
        onPbFrequencyChange={setPbFrequency}
        packageSplit={packageSplit}
        darkMode={darkMode}
      />

      <div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h4
            className={`text-sm font-semibold ${
              darkMode ? "text-gray-100" : "text-[hsl(var(--ds-fg))]"
            }`}
          >
            Calculation Results
          </h4>
          {structureModeLabel ? (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                structureRequiresManualInput(structure)
                  ? "bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning))]"
                  : "bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]"
              }`}
            >
              {structureModeLabel}
            </span>
          ) : null}
        </div>
        {!snapshotHydrated && structureRequiresManualInput(structure) ? (
          <p className="mb-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">Loading saved component amounts…</p>
        ) : null}
        {!ctcMatch.ok ? (
          <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-950">
            {ctcMatch.matchedLabel} ₹{ctcMatch.matchedAnnual.toLocaleString("en-IN")} does not match{" "}
            {ctcMatch.profileLabel} ₹
            {ctcMatch.targetAnnual.toLocaleString("en-IN")} (difference ₹
            {Math.abs(ctcMatch.delta).toLocaleString("en-IN")}). Enter full monthly amounts (e.g. Basic
            10000 → Annual 1,20,000), then tab/click away to apply.
          </p>
        ) : null}
        {Object.keys(formulaOverrides).length ? (
          <p className="mb-3 rounded-md border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.1)] px-3 py-2 text-[12px] text-[hsl(var(--ds-fg))]">
            {Object.keys(formulaOverrides).length} formula component(s) overridden for this employee —
            Special Allowance absorbs the difference. Salary Revision will ask to Keep or go Back to formula.
          </p>
        ) : null}
        {overrideDraft ? (
          <div className="mb-3 space-y-2 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3">
            <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">
              Override {overrideDraft.name}
              <span className="ml-2 text-xs font-normal text-[hsl(var(--ds-muted-fg))]">
                Formula value ₹{Number(overrideDraft.formulaAmount || 0).toLocaleString("en-IN")}/mo
              </span>
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_1fr]">
              <label className="text-xs text-[hsl(var(--ds-muted-fg))]">
                Monthly amount
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={overrideDraft.amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
                    setOverrideDraft((d) => ({ ...d, amount: v }));
                  }}
                  className="mt-1 w-full rounded border border-[hsl(var(--ds-border))] bg-white px-2 py-1 text-right text-sm tabular-nums outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                />
              </label>
              <label className="text-xs text-[hsl(var(--ds-muted-fg))]">
                Reason (mandatory, kept in audit)
                <input
                  type="text"
                  value={overrideDraft.reason}
                  maxLength={300}
                  onChange={(e) => setOverrideDraft((d) => ({ ...d, reason: e.target.value }))}
                  className="mt-1 w-full rounded border border-[hsl(var(--ds-border))] bg-white px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2">
              {overrideDraft.isExisting ? (
                <DsButton size="sm" variant="outline" className="mr-auto" onClick={resetOverrideDraft}>
                  Reset to formula
                </DsButton>
              ) : null}
              <DsButton size="sm" variant="ghost" onClick={() => setOverrideDraft(null)}>
                Cancel
              </DsButton>
              <DsButton size="sm" onClick={applyOverrideDraft}>
                Apply override
              </DsButton>
            </div>
          </div>
        ) : null}
        {breakdown ? (
          <SalaryStructureCalculationResults
            earningsList={breakdown.earningsList}
            benefitsList={breakdown.benefitsList}
            reimbursementsList={breakdown.reimbursementsList}
            deductionsList={breakdown.deductionsList}
            employerList={breakdown.employerList}
            statutoryDeductionsList={breakdown.statutoryDeductionsList}
            statutoryEmployerList={breakdown.statutoryEmployerList}
            totals={breakdown.totals}
            packageMode={mode}
            emptyMessage="No components in the assigned structure."
            onManualAmountChange={structureLock.locked ? undefined : handleManualAmountChange}
            draftAmounts={manualOverrides}
            formulaOverride={formulaOverrideProps}
          />
        ) : null}
      </div>
    </div>
  );
};

export default EmployeeSalaryStructureDesigner;
